import { Injectable } from '@angular/core';
import { GoogleMap, LatLngBounds } from '@capacitor/google-maps';
import {
  CameraConfig,
  CameraIdleCallbackData,
  LatLngBoundsInterface,
  MapClickCallbackData
} from '@capacitor/google-maps/dist/typings/definitions';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MSCameraConfig, MSLatLng, MSLatLngBounds, MSMapType } from 'src/app/models/map.model';
import { CONSTANTS } from 'src/app/services/config.service';
import { msMapTypeToMapType } from 'src/app/utils/google-map/google-map.utils';
import { Circle } from '../circle/circle';
import { CircleConfig } from '../circle/circle.config';
import { getBoundsAroundPoints, getCameraConfigForPoints } from '../google-maps-zoom-to-points.utils';
import { Marker } from '../marker/marker';
import { MarkerConfig } from '../marker/marker.config';
import { CameraData } from '../models/camera';
import { Polygon } from '../polygon/polygon';
import { PolygonConfig } from '../polygon/polygon.config';
import { Polyline } from '../polyline/polyline';
import { PolylineConfig } from '../polyline/polyline.config';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsControllerFactory {
  constructor() {}

  async create(map: GoogleMap): Promise<GoogleMapsController> {
    const controller = await GoogleMapsController.create(map);
    return controller;
  }
}

export class GoogleMapsController {
  private _onCameraIdleSignal = new Subject<void>();
  readonly onCameraIdleSignal = this._onCameraIdleSignal.asObservable();

  private _cameraState: BehaviorSubject<CameraData>;
  readonly cameraState: Observable<CameraData>;

  private _onMapClickSignal = new Subject<MSLatLng>();
  readonly onMapClickSignal = this._onMapClickSignal.asObservable();

  private constructor(private map: GoogleMap) {
    const initCameraData = {
      center: CONSTANTS.MAPS.DEFAULT_MAP_CENTER,
      zoom: CONSTANTS.MAPS.DEFAULT_MAP_ZOOM
    };
    this._cameraState = new BehaviorSubject<CameraData>(initCameraData);
    this.cameraState = this._cameraState.asObservable();
  }

  async setMapType(mapType: MSMapType): Promise<void> {
    const mapTypeActual = msMapTypeToMapType(mapType);
    await this.map.setMapType(mapTypeActual);
  }

  async addMarker(config: MarkerConfig): Promise<Marker> {
    const marker = await Marker.create(this.map, config);
    return marker;
  }

  async addPolyline(config: PolylineConfig): Promise<Polyline> {
    const polyline = await Polyline.create(this.map, config);
    return polyline;
  }

  async addPolygon(config: PolygonConfig): Promise<Polygon> {
    const polygon = await Polygon.create(this.map, config);
    return polygon;
  }

  async addCircle(config: CircleConfig): Promise<Circle> {
    const circle = await Circle.create(this.map, config);
    return circle;
  }

  async setZoom(zoom: number): Promise<void> {
    const config: CameraConfig = {
      zoom
    };
    await this.map.setCamera(config);
  }

  async getZoom(): Promise<number> {
    const cameraData = await this._cameraState.getValue();
    const zoom = cameraData.zoom;

    return zoom;
  }

  async setCenter(coordinate: MSLatLng): Promise<void> {
    const cameraConfig: CameraConfig = {
      coordinate
    };
    await this.map.setCamera(cameraConfig);
  }

  async getCenter(): Promise<MSLatLng> {
    const bounds = await this.map.getMapBounds();
    const center = bounds.center;

    return center;
  }

  async fitBounds(points: MSLatLng[]): Promise<void> {
    const msBounds: MSLatLngBounds = getBoundsAroundPoints(points);
    const boundsInterface: LatLngBoundsInterface = {
      northeast: msBounds.northeast,
      center: msBounds.center,
      southwest: msBounds.southwest
    };
    const bounds = new LatLngBounds(boundsInterface);

    await this.map.fitBounds(bounds);
  }

  async zoomToPoints(points: MSLatLng[]): Promise<void> {
    const config = await this.getCameraConfigForPoints(points);
    await this.map.setCamera(config);
  }

  async getCameraConfigForPoints(points: MSLatLng[]): Promise<MSCameraConfig> {
    const zoom = await this.getZoom();
    const bounds = await this.map.getMapBounds();
    const config = getCameraConfigForPoints(zoom, bounds, points);

    return config;
  }

  static async create(map: GoogleMap): Promise<GoogleMapsController> {
    const controller = new GoogleMapsController(map);

    await controller.map.setOnCameraIdleListener((_: CameraIdleCallbackData) => {
      controller._onCameraIdleSignal.next();
    });

    await controller.map.setOnBoundsChangedListener((data: CameraIdleCallbackData) => {
      const cameraData: CameraData = {
        center: data.bounds.center,
        zoom: data.zoom
      };
      controller._cameraState.next(cameraData);
    });

    await controller.map.setOnMapClickListener((data: MapClickCallbackData) => {
      const latLng: MSLatLng = {
        lat: data.latitude,
        lng: data.longitude
      };
      controller._onMapClickSignal.next(latLng);
    });

    return controller;
  }
}
